import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Pencil, Plus } from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_CATALOG_BILLING_TYPES,
  NR2J_VAT_OPTIONS,
  centsToEuros,
  eurosToCents,
  formatCentsAsEuros,
  nr2jBillingTypeLabel,
  nr2jVatLabel,
} from '../../utils/nr2jConstants';
import { readAdminSessionUser } from '../../utils/settingsSessionUser.js';

const emptyForm = {
  code: '',
  name: '',
  description: '',
  priceEuros: '',
  vatRateBps: 2000,
  billingType: 'ONE_TIME',
  active: true,
};

function canEditCatalog() {
  const user = readAdminSessionUser();
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  const perms = Array.isArray(user.platformPermissions) ? user.platformPermissions : [];
  return perms.includes('NR2J_CATALOG_EDIT');
}

export default function Nr2jCatalog() {
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const editable = canEditCatalog();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listNr2jCatalog();
      setItems(res?.items || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger le catalogue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    if (!editable) return;
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  }

  function openEdit(item) {
    if (!editable) return;
    setEditing(item);
    setForm({
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
      priceEuros: centsToEuros(item.unitPriceHtCents),
      vatRateBps: item.vatRateBps ?? 2000,
      billingType: item.billingType || 'ONE_TIME',
      active: item.active !== false,
    });
    setModalOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        unitPriceHtCents: eurosToCents(form.priceEuros),
        vatRateBps: Number(form.vatRateBps),
        billingType: form.billingType,
        active: form.active,
      };
      if (editing) {
        await apiService.updateNr2jCatalogItem(editing.id, payload);
        toast.success('Élément mis à jour');
      } else {
        await apiService.createNr2jCatalogItem(payload);
        toast.success('Élément créé');
      }
      setModalOpen(false);
      load();
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(item) {
    if (!editable) return;
    if (!window.confirm(`Désactiver « ${item.name} » ? L’élément restera visible mais ne sera plus proposé.`)) {
      return;
    }
    try {
      await apiService.updateNr2jCatalogItem(item.id, { active: false });
      toast.success('Élément désactivé');
      load();
    } catch (error) {
      toast.error(error?.message || 'Échec');
    }
  }

  return (
    <div className="space-y-6" data-testid="nr2j-catalog">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalogue commercial</h1>
          <p className="text-sm text-gray-600">
            Références tarifaires NR2J utilisables dans les devis.
          </p>
        </div>
        {editable ? (
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
        >
          <Plus className="h-4 w-4" /> Nouvel élément
        </button>
        ) : (
          <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600">
            Lecture seule
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <BookOpen className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-3 text-sm text-gray-600">Aucun élément catalogue.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Référence</th>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Prix HT</th>
                <th className="px-4 py-3">TVA</th>
                <th className="px-4 py-3">Actif</th>
                {editable ? <th className="px-4 py-3">Actions</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={item.id} className={item.active === false ? 'bg-gray-50 opacity-75' : ''}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{item.code}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-gray-600">{nr2jBillingTypeLabel(item.billingType)}</td>
                  <td className="px-4 py-3 text-gray-600">{formatCentsAsEuros(item.unitPriceHtCents)} €</td>
                  <td className="px-4 py-3 text-gray-600">{nr2jVatLabel(item.vatRateBps)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${
                        item.active !== false
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                          : 'border-gray-200 bg-gray-100 text-gray-600'
                      }`}
                    >
                      {item.active !== false ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  {editable ? (
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="inline-flex items-center gap-1 text-xs text-indigo-700"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifier
                      </button>
                      {item.active !== false ? (
                        <button
                          type="button"
                          onClick={() => deactivate(item)}
                          className="text-xs text-rose-700"
                        >
                          Désactiver
                        </button>
                      ) : null}
                    </div>
                  </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            aria-label="Fermer"
            onClick={() => setModalOpen(false)}
          />
          <form
            onSubmit={handleSubmit}
            className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {editing ? 'Modifier l’élément' : 'Nouvel élément catalogue'}
            </h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm sm:col-span-1">
                <span className="text-gray-500">Code *</span>
                <input
                  required
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                />
              </label>
              <label className="block text-sm sm:col-span-1">
                <span className="text-gray-500">Nom *</span>
                <input
                  required
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-gray-500">Description</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-500">Prix HT (€) *</span>
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={form.priceEuros}
                  onChange={(e) => setForm((f) => ({ ...f, priceEuros: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-500">TVA *</span>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={form.vatRateBps}
                  onChange={(e) => setForm((f) => ({ ...f, vatRateBps: Number(e.target.value) }))}
                >
                  {NR2J_VAT_OPTIONS.map((v) => (
                    <option key={v.bps} value={v.bps}>{v.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-gray-500">Type de facturation *</span>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={form.billingType}
                  onChange={(e) => setForm((f) => ({ ...f, billingType: e.target.value }))}
                >
                  {NR2J_CATALOG_BILLING_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Actif
              </label>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg border px-3 py-2 text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
